import type { CSSProperties } from 'react';

import { DEFAULT_BRANDING } from 'config/defaultBranding';
import type { BrandingSettings } from 'types';

/**
 * Typography shared by the editor page and every template preview.
 * The root uses the template font. Headings inherit it so a font on specific
 * text is kept, and app-wide Montserrat heading rules do not replace it.
 */

type DocumentBranding = Partial<BrandingSettings> | null | undefined;

function cssNumber(value: unknown, fallback: number, min: number, max: number): number {
    const number = typeof value === 'number' ? value : Number(value);
    if (!Number.isFinite(number)) return fallback;
    return Math.min(max, Math.max(min, number));
}

function cssColor(value: unknown, fallback: string): string {
    if (typeof value !== 'string') return fallback;
    const trimmed = value.trim();
    return /^#(?:[0-9a-f]{3}|[0-9a-f]{4}|[0-9a-f]{6}|[0-9a-f]{8})$/i.test(trimmed)
        ? trimmed
        : fallback;
}

const GENERIC_FAMILIES = new Set([
    'serif',
    'sans-serif',
    'monospace',
    'cursive',
    'fantasy',
    'system-ui',
    'ui-serif',
    'ui-sans-serif',
    'ui-monospace',
    'ui-rounded',
    'emoji',
    'math',
    'fangsong',
]);

const FAMILY_NAME = /^[A-Za-z0-9][A-Za-z0-9 -]{0,80}$/;

function decodeFontEntities(value: string): string {
    return value
        .replace(/&quot;|&#0*34;/gi, '"')
        .replace(/&apos;|&#0*39;/gi, "'")
        .replace(/&amp;/gi, '&');
}

function splitFontList(value: string): string[] {
    const parts: string[] = [];
    let current = '';
    let quote: '"' | "'" | null = null;
    for (const char of value) {
        if (quote) {
            if (char === quote) quote = null;
            else current += char;
            continue;
        }
        if (char === '"' || char === "'") {
            quote = char;
            continue;
        }
        if (char === ',') {
            const trimmed = current.trim();
            if (trimmed) parts.push(trimmed);
            current = '';
            continue;
        }
        current += char;
    }
    const trimmed = current.trim();
    if (trimmed) parts.push(trimmed);
    return parts;
}

export function fontFamilyLabel(value: unknown): string | null {
    if (typeof value !== 'string') return null;
    const parts = splitFontList(decodeFontEntities(value.trim()));
    const primary = parts.find((part) => !GENERIC_FAMILIES.has(part.toLowerCase()));
    if (!primary || !FAMILY_NAME.test(primary)) return null;
    return primary;
}

function explicitGeneric(value: string): string | null {
    const parts = splitFontList(decodeFontEntities(value.trim()));
    for (let index = parts.length - 1; index >= 0; index -= 1) {
        const lower = parts[index].toLowerCase();
        if (GENERIC_FAMILIES.has(lower)) return lower;
    }
    return null;
}

function genericFamily(fontFamily: string): string {
    if (/courier|mono/i.test(fontFamily)) return 'monospace';
    if (/times|georgia|garamond|palatino/i.test(fontFamily)) return 'serif';
    return 'sans-serif';
}

function formatPt(value: number): string {
    const rounded = Math.round(value * 100) / 100;
    return `${rounded}pt`;
}

interface ResolvedDocumentBranding {
    fontFamily: string;
    quotedFontFamily: string;
    bodyFontSize: number;
    headingFontSize: number;
    lineHeight: number;
    textColor: string;
    backgroundColor: string;
    primaryColor: string;
    marginTop: number;
    marginRight: number;
    marginBottom: number;
    marginLeft: number;
    paragraphSpacingBefore: number;
    paragraphSpacingAfter: number;
}

function resolveDocumentBranding(branding: DocumentBranding): ResolvedDocumentBranding {
    const requestedFamily = branding?.fontFamily;
    const fontFamily = fontFamilyLabel(requestedFamily) ?? DEFAULT_BRANDING.fontFamily;
    return {
        fontFamily,
        quotedFontFamily: quoteFontFamily(
            fontFamilyLabel(requestedFamily) ? requestedFamily : fontFamily
        ),
        bodyFontSize: cssNumber(branding?.bodyFontSize, DEFAULT_BRANDING.bodyFontSize, 8, 72),
        headingFontSize: cssNumber(
            branding?.headingFontSize,
            DEFAULT_BRANDING.headingFontSize,
            8,
            96
        ),
        lineHeight: cssNumber(branding?.lineHeight, DEFAULT_BRANDING.lineHeight, 0.8, 3),
        textColor: cssColor(branding?.textColor, DEFAULT_BRANDING.textColor),
        backgroundColor: cssColor(branding?.backgroundColor, DEFAULT_BRANDING.backgroundColor),
        primaryColor: cssColor(branding?.primaryColor, DEFAULT_BRANDING.primaryColor),
        marginTop: cssNumber(branding?.marginTop, DEFAULT_BRANDING.marginTop, 0, 3),
        marginRight: cssNumber(branding?.marginRight, DEFAULT_BRANDING.marginRight, 0, 3),
        marginBottom: cssNumber(branding?.marginBottom, DEFAULT_BRANDING.marginBottom, 0, 3),
        marginLeft: cssNumber(branding?.marginLeft, DEFAULT_BRANDING.marginLeft, 0, 3),
        paragraphSpacingBefore: cssNumber(
            branding?.paragraphSpacingBefore,
            DEFAULT_BRANDING.paragraphSpacingBefore,
            0,
            72
        ),
        paragraphSpacingAfter: cssNumber(
            branding?.paragraphSpacingAfter,
            DEFAULT_BRANDING.paragraphSpacingAfter,
            0,
            72
        ),
    };
}

export function quoteFontFamily(fontFamily?: string | null): string {
    const label = fontFamilyLabel(fontFamily);
    const name = label ?? DEFAULT_BRANDING.fontFamily;
    const generic =
        (label && typeof fontFamily === 'string' && explicitGeneric(fontFamily)) ||
        genericFamily(name);
    return `'${name}', ${generic}`;
}

const HTML_ENTITY = /^&(?:#x[\da-f]+|#\d+|[a-z][a-z0-9]+);/i;

function readDeclarationValue(
    style: string,
    property: string
): { start: number; end: number; value: string } | null {
    const match = new RegExp(`${property}\\s*:`, 'i').exec(style);
    if (!match) return null;
    let index = match.index + match[0].length;
    let value = '';
    while (index < style.length) {
        if (style[index] === '&') {
            const entity = HTML_ENTITY.exec(style.slice(index));
            if (entity) {
                value += entity[0];
                index += entity[0].length;
                continue;
            }
        }
        if (style[index] === ';') break;
        value += style[index];
        index += 1;
    }
    return { start: match.index, end: index, value: value.trim() };
}

function nextFontFamily(
    style: string,
    from: number
): { start: number; end: number; quoted: string | null } | null {
    const found = readDeclarationValue(style.slice(from), 'font-family');
    if (!found) return null;
    return {
        start: from + found.start,
        end: from + found.end,
        quoted: fontFamilyLabel(found.value) ? quoteFontFamily(found.value) : null,
    };
}

export function fontFamilyFromStyle(style: string | null | undefined): string | null {
    if (!style) return null;
    let cursor = 0;
    let quoted: string | null = null;
    while (cursor < style.length) {
        const declaration = nextFontFamily(style, cursor);
        if (!declaration) break;
        if (declaration.quoted) quoted = declaration.quoted;
        cursor = declaration.end + 1;
    }
    return quoted;
}

export function normalizeFontFamilyStyles(html: string): string {
    if (!html || !/font-family\s*:/i.test(html)) return html;
    return html.replace(
        /style\s*=\s*(["'])([\s\S]*?)\1/gi,
        (match, _attributeQuote: string, style: string) => {
            if (!/font-family\s*:/i.test(style)) return match;
            let nextStyle = style;
            let changed = false;
            let cursor = 0;
            while (cursor < nextStyle.length) {
                const declaration = nextFontFamily(nextStyle, cursor);
                if (!declaration) break;
                if (!declaration.quoted) {
                    cursor = declaration.end + 1;
                    continue;
                }
                const replacement = `font-family: ${declaration.quoted}`;
                const current = nextStyle.slice(declaration.start, declaration.end);
                if (current.trim() === replacement) {
                    cursor = declaration.end + 1;
                    continue;
                }
                nextStyle = `${nextStyle.slice(0, declaration.start)}${replacement}${nextStyle.slice(declaration.end)}`;
                changed = true;
                cursor = declaration.start + replacement.length + 1;
            }
            if (!changed) return match;
            const delimiter = nextStyle.includes('"') ? "'" : '"';
            if (delimiter === "'" && nextStyle.includes("'")) {
                return `style="${nextStyle.replace(/"/g, '&quot;')}"`;
            }
            return `style=${delimiter}${nextStyle}${delimiter}`;
        }
    );
}

export function getDocumentCanvasStyle(branding: DocumentBranding): CSSProperties {
    const resolved = resolveDocumentBranding(branding);
    const style: CSSProperties &
        Record<'--document-table-header-bg' | '--document-table-header-color', string> = {
        fontFamily: resolved.quotedFontFamily,
        fontSize: formatPt(resolved.bodyFontSize),
        lineHeight: resolved.lineHeight,
        color: resolved.textColor,
        backgroundColor: resolved.backgroundColor,
        padding: `${resolved.marginTop}in ${resolved.marginRight}in ${resolved.marginBottom}in ${resolved.marginLeft}in`,
        '--document-table-header-bg': resolved.primaryColor,
        '--document-table-header-color': '#ffffff',
    };
    return style;
}

export function buildDocumentContentCss(root: string, branding: DocumentBranding): string {
    const resolved = resolveDocumentBranding(branding);
    const before = formatPt(resolved.paragraphSpacingBefore);
    const after = formatPt(resolved.paragraphSpacingAfter);
    const heading = resolved.headingFontSize;
    const family = resolved.quotedFontFamily;

    return `
${root} {
  font-family: ${family};
  font-size: ${formatPt(resolved.bodyFontSize)};
  line-height: ${resolved.lineHeight};
  color: ${resolved.textColor};
}
${root} h1,
${root} h2,
${root} h3,
${root} h4,
${root} h5,
${root} h6 {
  font-family: inherit;
  line-height: ${resolved.lineHeight};
  color: inherit;
  letter-spacing: normal;
  font-weight: 700;
}
${root} h1 {
  font-size: ${formatPt(heading)};
  margin-top: ${before};
  margin-bottom: ${after};
}
${root} h2 {
  font-size: ${formatPt(heading * 0.85)};
  margin-top: ${before};
  margin-bottom: ${after};
}
${root} h3 {
  font-size: ${formatPt(heading * 0.75)};
  margin-top: ${before};
  margin-bottom: ${after};
}
${root} h4,
${root} h5,
${root} h6 {
  font-size: ${formatPt(resolved.bodyFontSize)};
  margin-top: ${before};
  margin-bottom: ${after};
}
${root} p {
  margin-top: ${before};
  margin-bottom: ${after};
}
${root} li > p,
${root} td p,
${root} th p {
  margin-top: 0;
  margin-bottom: 0;
}
${root} strong,
${root} b {
  font-weight: 700;
}
${root} em,
${root} i {
  font-style: italic;
}
${root} u {
  text-decoration: underline;
}
${root} s,
${root} strike,
${root} del {
  text-decoration: line-through;
}
${root} blockquote {
  margin-top: ${before};
  margin-right: 0;
  margin-bottom: ${after};
  margin-left: 0;
  padding-left: 12pt;
  border-left: 3px solid ${resolved.primaryColor};
}
${root} code {
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 0.92em;
  background-color: rgba(0, 0, 0, 0.06);
  padding: 0.1em 0.25em;
  border-radius: 3px;
}
${root} a {
  text-decoration: underline;
}
${root} hr {
  border: 0;
  border-top: 1px solid #d1d5db;
  margin: 12pt 0;
}
${root} img {
  max-width: 100%;
  height: auto;
}
${root} table {
  border-collapse: collapse;
  width: 100%;
  margin: 12pt 0;
}
${root} table td,
${root} table th {
  border: 1px solid #ddd;
  padding: 8px;
  vertical-align: top;
}
${root} table th {
  background-color: ${resolved.primaryColor};
  color: #ffffff;
  font-weight: 700;
  text-align: left;
}
`.trim();
}
